import { useEffect, useMemo, useRef, useState } from "react";
import { unstable_useBlocker as rrUseBlocker, useBeforeUnload } from "react-router-dom";
import MessageModal, { MessageModalType } from "../components/modals/MessageModal";

function useSafeBlocker(when) {
  try {
    const blocker = rrUseBlocker ? rrUseBlocker(Boolean(when)) : null;
    return blocker ?? null;
  }
  catch {
    return null;
  }
}

/**
 * Prompt the user when navigating away with unsaved changes.
 * - Blocks in-app navigation via Data Router blocker.
 * - Blocks tab close / refresh via beforeunload.
 */
export default function useUnsavedChangesPrompt(
  unsavedChanges,
  options
) {
  const {
    title = "Leave this page?",
    message = "You have unsaved changes. If you leave now, your changes will be lost. Do you want to continue?",
    modalProps
  } = options ?? {};

  const [showModal, setShowModal] = useState(false);
  const blockerRef = useRef(null);

  useBeforeUnload(
    unsavedChanges
      ? (e) => {
        e.preventDefault();
        e.returnValue = "";
      }
      : null,
    { capture: true }
  );

  const blocker = useSafeBlocker(unsavedChanges);

  useEffect(() => {
    if (!blocker) {
      return;
    }

    if (blocker.state === "blocked") {
      blockerRef.current = blocker;
      setShowModal(true);
    }
    else if (blocker.state === "unblocked") {
      blockerRef.current = null;
      setShowModal(false);
    }
  }, [blocker && blocker.state]);

  function onStay() {
    if (blockerRef.current) {
      blockerRef.current.reset(); // cancel navigation
    }
    setShowModal(false);
  }

  function onLeave() {
    if (blockerRef.current) {
      blockerRef.current.proceed(); // continue navigation
    }
    setShowModal(false);
  }

  const renderModal = useMemo(() => {
    if (!showModal) {
      return null;
    }

    return (
      <MessageModal
        title={title}
        type={MessageModalType.Question}
        onClick={(yes) => {
          if (yes) {
            onLeave();
          }
          else {
            onStay();
          }
        }}
        size="m"
        {...(modalProps ?? {})}
      >
        <p>{message}</p>
      </MessageModal>
    );
  }, [showModal, title, message]);

  return {
    isBlocking: Boolean(unsavedChanges),
    renderModal
  };
}